import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Stack,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import TableRestaurantRoundedIcon from "@mui/icons-material/TableRestaurantRounded";
import DisplaySettingsRoundedIcon from "@mui/icons-material/DisplaySettingsRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import ExtensionRoundedIcon from "@mui/icons-material/ExtensionRounded";
import { useDispatch, useSelector } from "src/redux/store";
import { getAddOns } from "src/redux/slices/addOns";
import { resolveFinalPrice, formatIvaCaption } from "src/utils/pricing";
import { isGifted } from "src/utils/addOnGifts";

// Grid reusable de add-ons contratables. Se usa en el wizard, en el
// portal Billing y (futuro) en el modal de sesiones. El padre controla
// la selección y decide cuándo persistir (SetAddOn, checkout, etc.).
//
// Iconografía por `kind` + fallback por nombre para dar contexto visual
// sin obligar al fundador a subir iconos personalizados por add-on.

const KIND_ICON = {
  device: DevicesRoundedIcon,
  tables: TableRestaurantRoundedIcon,
};

const NAME_ICON_HINTS = [
  { re: /kds|cocina|pantalla/i, icon: DisplaySettingsRoundedIcon },
  { re: /reserva/i, icon: EventAvailableRoundedIcon },
  { re: /escandallo|stock|compra|inventari/i, icon: InventoryRoundedIcon },
  { re: /rese[nñ]a|reputaci|google/i, icon: StarRateRoundedIcon },
];

function iconFor(addOn) {
  if (addOn.kind && KIND_ICON[addOn.kind]) return KIND_ICON[addOn.kind];
  const hint = NAME_ICON_HINTS.find((h) => h.re.test(addOn.name || ""));
  return hint ? hint.icon : ExtensionRoundedIcon;
}

export default function AddOnsPicker({
  selectedIds,
  onChange,
  activeIds = [],
  // Nombres de add-ons regalados por el admin (utils/addOnGifts): ya activos, sin precio.
  giftedNames = new Set(),
  filter,
  includedCount = 0,
  cycle = "monthly",
  emptyLabel = "No hay add-ons disponibles.",
}) {
  const dispatch = useDispatch();
  const { addOns } = useSelector((s) => s.addOns);

  useEffect(() => {
    if (!addOns || addOns.length === 0) dispatch(getAddOns(true));
  }, [dispatch, addOns]);

  // Filtramos por `cycle` para mostrar SOLO los add-ons del ciclo del plan
  // actual (o del que se está eligiendo en el wizard). Los add-ons con
  // `billingCycle == null` (legacy pre-rediseño) se tratan como "monthly"
  // para no romper compat. Cada AddOn tiene hermano del otro ciclo — al
  // cambiar de plan el backend hace swap silenciosamente vía sibling.
  const list = (addOns || [])
    .filter((a) => a.isActive !== false)
    .filter((a) => (a.billingCycle || "monthly") === cycle)
    .filter(filter || (() => true));

  const toggle = (id) => {
    const set = new Set(selectedIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange([...set]);
  };

  if (list.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary", py: 2 }}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Grid container spacing={2}>
      {list.map((a) => {
        const selected = selectedIds.includes(a.id);
        const gifted = isGifted(a, giftedNames);
        const active = activeIds.includes(a.id) || gifted;
        const IconComp = iconFor(a);
        const disabled = active;
        // Los primeros `includedCount` seleccionados (por orden de
        // selección) son "gratis con tu plan" — se decoran con badge
        // verde y precio tachado.
        const selectedIndex = selected ? selectedIds.indexOf(a.id) : -1;
        const isFreeByPlan = selected && selectedIndex >= 0 && selectedIndex < includedCount;
        return (
          <Grid item xs={12} sm={6} md={4} key={a.id}>
            <Card
              onClick={() => !disabled && toggle(a.id)}
              sx={{
                position: "relative",
                cursor: disabled ? "default" : "pointer",
                borderRadius: 2,
                border: "2px solid",
                borderColor: selected
                  ? "primary.main"
                  : active
                  ? "success.light"
                  : "divider",
                bgcolor: selected ? "primary.lighter" : "background.paper",
                transition: "all 0.15s",
                overflow: "hidden",
                "&:hover": disabled
                  ? undefined
                  : { borderColor: "primary.light", transform: "translateY(-2px)" },
              }}
            >
              {/* Cinta color en el lateral izquierdo cuando está seleccionado */}
              {selected && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: "0 auto 0 0",
                    width: 4,
                    bgcolor: "primary.main",
                  }}
                />
              )}
              <CardContent sx={{ pl: selected ? 2.5 : 2, py: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.5,
                      bgcolor: selected ? "primary.main" : "primary.lighter",
                      color: selected ? "primary.contrastText" : "primary.dark",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <IconComp fontSize="medium" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap>
                        {a.name}
                      </Typography>
                      {active ? (
                        <Chip
                          size="small"
                          color="success"
                          label={gifted ? "Incluido (regalo)" : "Ya activo"}
                          sx={{ fontWeight: 700, height: 22 }}
                        />
                      ) : selected ? (
                        <CheckCircleRoundedIcon
                          fontSize="small"
                          sx={{ color: "primary.main" }}
                        />
                      ) : (
                        <RadioButtonUncheckedRoundedIcon
                          fontSize="small"
                          sx={{ color: "text.disabled" }}
                        />
                      )}
                    </Stack>
                    {gifted ? (
                      <Typography variant="caption" sx={{ display: "block", color: "success.dark", fontWeight: 700, mb: 0.5 }}>
                        Incluido sin coste
                      </Typography>
                    ) : isFreeByPlan ? (
                      <>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: "primary.dark", fontWeight: 700 }}
                          >
                            {resolveFinalPrice(a).toFixed(2)} €/mes
                          </Typography>
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label="Recomendado con tu plan"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                          />
                        </Stack>
                        {formatIvaCaption(a) && (
                          <Typography variant="caption" sx={{ display: "block", color: "text.disabled", fontSize: 10, mb: 0.5 }}>
                            {formatIvaCaption(a)}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Box sx={{ mb: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", color: "primary.dark", fontWeight: 700 }}
                        >
                          {resolveFinalPrice(a).toFixed(2)} €/mes
                        </Typography>
                        {formatIvaCaption(a) && (
                          <Typography variant="caption" sx={{ display: "block", color: "text.disabled", fontSize: 10 }}>
                            {formatIvaCaption(a)}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {a.description && (
                      <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.4 }}>
                        {a.description}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

AddOnsPicker.propTypes = {
  selectedIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
  activeIds: PropTypes.arrayOf(PropTypes.number),
  filter: PropTypes.func,
  /**
   * Los primeros N seleccionados (por orden de click) se pintan como
   * "GRATIS con tu plan". Los siguientes mantienen su precio normal.
   * Default 0 = ningún add-on incluido (Starter y sin plan).
   */
  includedCount: PropTypes.number,
  /**
   * Ciclo de facturación por el que se filtra el listado — solo se pintan
   * los add-ons cuyo `billingCycle` coincida. Cada AddOn tiene hermano
   * del otro ciclo (mensual↔anual) y el backend swappea automáticamente
   * al cambiar de plan. Default "monthly".
   */
  cycle: PropTypes.oneOf(["monthly", "yearly"]),
  emptyLabel: PropTypes.string,
};
